<template>
  <div>
    <div v-if="isLoading" class="flex justify-center items-center py-8">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
    
    <div v-else-if="hasError" class="text-center py-8 text-red-500">
      {{ typeof hasError === 'string' ? hasError : 'An error occurred while loading data' }}
    </div>

    <div v-else>
    <div class="mb-4 flex justify-between items-center">
      <div class="relative w-64">
        <input
          v-model="searchQuery"
          @input="updateSearch"
          placeholder="Search..."
          class="w-full px-4 py-2 bg-background-primary dark:bg-background-primary-dark 
                 border border-border dark:border-border-dark rounded-md
                 text-text-primary dark:text-text-primary-dark
                 placeholder-text-muted dark:placeholder-text-muted-dark
                 focus:outline-none focus:ring-2 focus:ring-primary dark:focus:ring-primary-light
                 focus:border-transparent transition-colors duration-200"
        />
        <div class="absolute inset-y-0 right-0 flex items-center pr-3">
          <svg class="w-5 h-5 text-text-muted dark:text-text-muted-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </div>
      <div class="flex items-center space-x-2">
        <label for="entriesPerPage" class="text-text-secondary dark:text-text-secondary-dark text-sm">Entries per page:</label>
        <select
          id="entriesPerPage"
          v-model="entriesPerPage"
          @change="updateEntriesPerPage"
          class="px-3 py-2 bg-background-primary dark:bg-background-primary-dark 
                 border border-border dark:border-border-dark rounded-md
                 text-text-primary dark:text-text-primary-dark
                 focus:outline-none focus:ring-2 focus:ring-primary dark:focus:ring-primary-light
                 focus:border-transparent transition-colors duration-200"
        >
          <option :value="10">10</option>
          <option :value="25">25</option>
          <option :value="50">50</option>
          <option :value="100">100</option>
        </select>
      </div>
    </div>

    <div class="overflow-x-auto rounded-lg border border-border dark:border-border-dark">
      <table class="min-w-full divide-y divide-border dark:divide-border-dark">
        <thead class="bg-background-secondary dark:bg-background-secondary-dark">
          <tr>
            <th
              v-for="column in columns"
              :key="column.key"
                @click="column.sortable !== false ? sort(column) : null"
                :class="[
                  'px-6 py-3 text-left text-xs font-medium text-text-secondary dark:text-text-secondary-dark uppercase tracking-wider',
                  column.sortable !== false ? 'cursor-pointer hover:bg-background-tertiary dark:hover:bg-background-tertiary-dark' : '',
                  'transition-colors duration-200'
                ]"
            >
              <div class="flex items-center space-x-1">
                <span>{{ column.label }}</span>
                  <span v-if="column.sortable !== false && orderBy === column.key" class="text-primary dark:text-primary-light">
                  {{ sortDirection === 'asc' ? '▲' : '▼' }}
                </span>
              </div>
            </th>
          </tr>
        </thead>
        <tbody class="bg-background-primary dark:bg-background-primary-dark divide-y divide-border dark:divide-border-dark">
            <tr v-if="tableData.length === 0" class="hover:bg-background-secondary dark:hover:bg-background-secondary-dark">
              <td :colspan="columns.length" class="px-6 py-4 text-center text-sm text-text-secondary dark:text-text-secondary-dark">
                No data available
              </td>
            </tr>
          <tr 
              v-for="item in tableData" 
            :key="item.id"
            @click="$emit('rowClick', item)"
            class="hover:bg-background-secondary dark:hover:bg-background-secondary-dark cursor-pointer transition-colors duration-200"
          >
            <td
              v-for="column in columns"
              :key="column.key"
              class="px-6 py-4 whitespace-nowrap text-sm text-text-primary dark:text-text-primary-dark"
                :class="typeof column.class === 'function' ? column.class(getNestedValue(item, column.key)) : column.class"
            >
              <template v-if="column.slot && $slots[column.slot]">
                <slot :name="column.slot" :row="item" />
              </template>
              <template v-else-if="column.formatter">
                {{ column.formatter(getNestedValue(item, column.key)) }}
              </template>
              <template v-else>
                {{ getNestedValue(item, column.key) }}
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="mt-4 flex justify-between items-center">
      <div v-if="totalEntries > 0" class="text-sm text-text-secondary dark:text-text-secondary-dark">
        Showing {{ startEntry }} to {{ endEntry }} of {{ totalEntries }} entries
      </div>
      <div v-else class="text-sm text-text-secondary dark:text-text-secondary-dark">
        No entries to display
      </div>
      <div class="flex space-x-2">
        <button
          @click="changePage(currentPage - 1)"
          :disabled="currentPage === 1"
          class="px-3 py-1.5 bg-background-primary dark:bg-background-primary-dark 
                 border border-border dark:border-border-dark rounded-md
                 text-text-primary dark:text-text-primary-dark
                 hover:bg-background-secondary dark:hover:bg-background-secondary-dark
                 focus:outline-none focus:ring-2 focus:ring-primary dark:focus:ring-primary-light
                 disabled:opacity-50 disabled:cursor-not-allowed
                 transition-colors duration-200"
        >
          Previous
        </button>
        <button
          @click="changePage(currentPage + 1)"
          :disabled="currentPage === totalPages"
          class="px-3 py-1.5 bg-background-primary dark:bg-background-primary-dark 
                 border border-border dark:border-border-dark rounded-md
                 text-text-primary dark:text-text-primary-dark
                 hover:bg-background-secondary dark:hover:bg-background-secondary-dark
                 focus:outline-none focus:ring-2 focus:ring-primary dark:focus:ring-primary-light
                 disabled:opacity-50 disabled:cursor-not-allowed
                 transition-colors duration-200"
        >
          Next
        </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue'
import { useStore } from 'vuex'

export default {
  name: 'DataTable',
  props: {
    columns: {
      type: Array,
      required: true,
      validator: (columns) => {
        return columns.every(col => 
          typeof col.key === 'string' && 
          typeof col.label === 'string' &&
          (!col.formatter || typeof col.formatter === 'function') &&
          (!col.class || typeof col.class === 'function' || typeof col.class === 'string')
        )
      }
    },
    resourceName: {
      type: String,
      required: false
    },
    data: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: [String, Object],
      default: null
    },
    pagination: {
      type: Object,
      default: () => ({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        entriesPerPage: 10,
        orderBy: 'id',
        sortDirection: 'asc'
      })
    }
  },
  emits: ['rowClick', 'selectCategory', 'page-change'],
  setup(props, { emit }) {
    const store = useStore()
    const searchQuery = ref('')
    const entriesPerPage = ref(props.pagination.entriesPerPage || 10)
    const internalCurrentPage = ref(props.pagination.currentPage || 1)
    let searchTimeout = null

    const getNestedValue = (obj, path) => {
      if (!obj) return null
      return path.split('.').reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : null
      }, obj)
    }

    const stateData = computed(() => props.resourceName ? store.state[props.resourceName] : null)
    const tableData = computed(() => {
      if (props.resourceName && stateData.value) {
        return stateData.value.data || []
      }
      return props.data || []
    })
    const currentPage = computed(() => {
      if (props.resourceName && stateData.value) {
        return stateData.value.currentPage || 1
      }
      return internalCurrentPage.value
    })
    const totalPages = computed(() => {
      if (props.resourceName && stateData.value) {
        return stateData.value.totalPages || 1
      }
      return props.pagination.totalPages || 1
    })
    const totalEntries = computed(() => {
      if (props.resourceName && stateData.value) {
        return stateData.value.totalEntries || 0
      }
      return props.pagination.totalItems || 0
    })
    const orderBy = computed(() => {
      if (props.resourceName && stateData.value) {
        return stateData.value.orderBy || 'id'
      }
      return props.pagination.orderBy || 'id'
    })
    const sortDirection = computed(() => {
      if (props.resourceName && stateData.value) {
        return stateData.value.sortDirection || 'asc'
      }
      return props.pagination.sortDirection || 'asc'
    })
    const isLoading = computed(() => {
      if (props.resourceName && stateData.value) {
        return stateData.value.loading || false
      }
      return props.loading || false
    })
    const hasError = computed(() => {
      if (props.resourceName && stateData.value) {
        return stateData.value.error || null
      }
      return props.error || null
    })

    const startEntry = computed(() => {
      if (totalEntries.value === 0) return 0
      return (currentPage.value - 1) * entriesPerPage.value + 1
    })

    const endEntry = computed(() => {
      if (totalEntries.value === 0) return 0
      return Math.min(currentPage.value * entriesPerPage.value, totalEntries.value)
    })

    const updateSearch = () => {
      // Clear any existing timeout
      if (searchTimeout) {
        clearTimeout(searchTimeout)
      }

      // Set a new timeout to trigger the search after 300ms of no typing
      searchTimeout = setTimeout(() => {
        internalCurrentPage.value = 1 // Reset to first page when searching
        if (props.resourceName) {
          store.dispatch(`${props.resourceName}/fetchData`, {
            tenantId: store.state.currentTenantId,
            page: 1, // Reset to first page when searching
            entriesPerPage: entriesPerPage.value,
            search: searchQuery.value,
            orderBy: orderBy.value,
            sortDirection: sortDirection.value
          })
        } else {
          emit('page-change', {
            page: 1, // Reset to first page when searching
            entriesPerPage: entriesPerPage.value,
            search: searchQuery.value,
            orderBy: orderBy.value,
            sortDirection: sortDirection.value
          })
        }
      }, 300)
    }

    const updateEntriesPerPage = () => {
      internalCurrentPage.value = 1 // Reset to first page when changing entries per page
      if (props.resourceName) {
        fetchData({ entriesPerPage: entriesPerPage.value, page: 1 })
      } else {
        emit('page-change', { 
          entriesPerPage: entriesPerPage.value, 
          page: 1,
          search: searchQuery.value,
          orderBy: orderBy.value,
          sortDirection: sortDirection.value
        })
      }
    }

    const sort = (column) => {
      if (column.sortable === false) return
      
      const newDirection = orderBy.value === column.key && sortDirection.value === 'asc' ? 'desc' : 'asc'
      
      if (props.resourceName) {
        store.commit(`${props.resourceName}/setOrderBy`, column.key)
        store.commit(`${props.resourceName}/setSortDirection`, newDirection)
        // Trigger a new fetch with updated sort parameters
        store.dispatch(`${props.resourceName}/fetchData`, {
          tenantId: store.state.currentTenantId,
          page: currentPage.value,
          entriesPerPage: entriesPerPage.value,
          search: searchQuery.value,
          orderBy: column.key,
          sortDirection: newDirection
        })
      } else {
        emit('page-change', {
          page: currentPage.value,
          entriesPerPage: entriesPerPage.value,
          search: searchQuery.value,
          orderBy: column.key,
          sortDirection: newDirection
        })
      }
    }

    const changePage = (page) => {
      internalCurrentPage.value = page
      if (props.resourceName) {
        fetchData({ page })
      } else {
        emit('page-change', { 
          page,
          entriesPerPage: entriesPerPage.value,
          search: searchQuery.value,
          orderBy: orderBy.value,
          sortDirection: sortDirection.value
        })
      }
    }

    const fetchData = (params = {}) => {
      if (!props.resourceName) return
      
      const currentTenantId = store.state.currentTenantId
      if (currentTenantId) {
        store.dispatch(`${props.resourceName}/fetchData`, {
          tenantId: currentTenantId,
          page: params.page || currentPage.value,
          entriesPerPage: params.entriesPerPage || entriesPerPage.value,
          search: searchQuery.value,
          orderBy: orderBy.value,
          sortDirection: sortDirection.value,
          ...params
        })
      }
    }

    watch(() => props.pagination.entriesPerPage, (newValue) => {
      if (newValue) {
        entriesPerPage.value = newValue
      }
    })

    watch(() => props.pagination.currentPage, (newValue) => {
      if (newValue) {
        internalCurrentPage.value = newValue
      }
    })

    onMounted(() => {
      if (props.resourceName) {
      fetchData()
      }
    })

    // Clean up the timeout when component is unmounted
    onMounted(() => {
      return () => {
        if (searchTimeout) {
          clearTimeout(searchTimeout)
        }
      }
    })

    return {
      searchQuery,
      entriesPerPage,
      tableData,
      currentPage,
      totalPages,
      totalEntries,
      orderBy,
      sortDirection,
      startEntry,
      endEntry,
      updateSearch,
      updateEntriesPerPage,
      sort,
      changePage,
      getNestedValue,
      isLoading,
      hasError
    }
  }
}
</script>

<style scoped>
table {
  @apply shadow-sm;
}
th {
  @apply transition-colors duration-150;
}
td {
  @apply transition-colors duration-150;
}
</style>